/**
 * Observed stock extensions: operations whose shortcuts and input rules ship inside stock Tiptap
 * packages run the bare mutation and report nothing. Each extension here `.extend()`s its stock
 * original and re-declares those bindings so the keyboard/markdown surfaces produce the same
 * events as the floating toolbar, with the same args and labeling discipline.
 *
 * The re-declared maps deliberately restate the stock keys rather than spreading the parent's:
 * a spread would keep an unobserved stock binding alive silently, and "every owned shortcut
 * declares its operation or its silence" is the rule that makes a dependency upgrade adding a new
 * binding a visible test failure instead of an unreported surface.
 *
 * Only *toggles* are re-declared. A binding with a hidden state machine (backspace over a widget,
 * list indent/outdent, hard break) stays stock: re-implementing it to attach a label would mean
 * owning semantics we do not own today, and a behavior regression is worse than a missing event.
 *
 * `extensions.ts` registers these in place of the stock originals. Everything else about the
 * stock extension — schema, plugins, commands — is inherited unchanged.
 */

import {
  Extension,
  InputRule,
  textblockTypeInputRule,
  type ChainedCommands,
  type KeyboardShortcutCommand,
} from "@tiptap/core"
import { Bold } from "@tiptap/extension-bold"
import { Heading } from "@tiptap/extension-heading"
import { Italic } from "@tiptap/extension-italic"
import { Paragraph } from "@tiptap/extension-paragraph"
import { Strike } from "@tiptap/extension-strike"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { UndoRedo } from "@tiptap/extensions"
import { Plugin, type Transaction } from "@tiptap/pm/state"
import { FlatListOrdered, FlatListTask, FlatListUnordered } from "tiptap-extension-flat-list"
import { reportOperation } from "../../analytics/operation-event"
import { OPERATION_CONTRACTS, type OperationContract } from "../../analytics/operations"
import type { ListKind } from "../../analytics/types"
import { LIST_TYPES, toggleBlockType } from "../../util/block-node-helpers"
import { getContainingCardId } from "../../util/card"
import { CpHighlight } from "../highlight/highlight-extension"
import { CpUnderline } from "../underline/underline-extension"

/**
 * Wrap one stock input rule so its transaction is honest. When the rule applies, `applyLabel`
 * labels the rule's transaction through the one producer API: the handler's `chain` prop is
 * bound to the very transaction Tiptap dispatches (its `run()` never dispatches), so
 * `chain().label(...)` stamps that transaction.
 */
function observedInputRule(
  rule: InputRule,
  applyLabel: (props: { tr: Transaction; chain: () => ChainedCommands }) => void,
): InputRule {
  return new InputRule({
    find: rule.find,
    undoable: rule.undoable,
    handler: (props) => {
      const { tr } = props.state
      const stepsBefore = tr.steps.length
      const result = rule.handler(props)
      // Mirrors Tiptap's own dispatch gate: a null return or a step-less transaction is a
      // cancelled rule, which dispatches nothing and must report nothing.
      if (result === null || tr.steps.length === stepsBefore) return result
      applyLabel({ tr, chain: props.chain })
      return result
    },
  })
}

/** The mark-toggle operations' shared contract shape: no args, operation + surface is the whole fact. */
type ToggleMarkOperation = OperationContract<null>

/** A mark extension's stock rules (`**…**`, `*…*`, `~~…~~`), each reporting its toggle. */
function markMarkdownRules(rules: InputRule[], operation: ToggleMarkOperation): InputRule[] {
  return rules.map((rule) =>
    observedInputRule(rule, ({ chain }) => chain().label(operation, "markdown", null).run()),
  )
}

/** A flat-list extension's stock `- `/`1. `/`[ ] ` rule, reporting `set-list` as `list`. */
function listMarkdownRules(rules: InputRule[], list: Exclude<ListKind, "none">): InputRule[] {
  return rules.map((rule) =>
    observedInputRule(rule, ({ chain }) =>
      chain().label(OPERATION_CONTRACTS.OP_SET_LIST, "markdown", { list }).run(),
    ),
  )
}

/**
 * Stock `UndoRedo`, with its keyboard map re-declared to report `undo`/`redo` — "successful
 * history only" — while the history plugin itself stays stock.
 */
export const ObservedUndoRedo = UndoRedo.extend({
  addKeyboardShortcuts() {
    return {
      "Mod-z": () =>
        this.editor.chain().label(OPERATION_CONTRACTS.OP_UNDO, "keyboard", null).undo().run(),
      "Shift-Mod-z": () =>
        this.editor.chain().label(OPERATION_CONTRACTS.OP_REDO, "keyboard", null).redo().run(),
      "Mod-y": () =>
        this.editor.chain().label(OPERATION_CONTRACTS.OP_REDO, "keyboard", null).redo().run(),
      // Russian keyboard layouts (mirrors the stock map).
      "Mod-я": () =>
        this.editor.chain().label(OPERATION_CONTRACTS.OP_UNDO, "keyboard", null).undo().run(),
      "Shift-Mod-я": () =>
        this.editor.chain().label(OPERATION_CONTRACTS.OP_REDO, "keyboard", null).redo().run(),
    }
  },
})

/** One mark extension's stock shortcut keys, each re-bound to a labeled `toggleMark` mutation. */
function markShortcuts(
  keys: readonly string[],
  mark: string,
  operation: ToggleMarkOperation,
): Record<string, KeyboardShortcutCommand> {
  return Object.fromEntries(
    keys.map((key): [string, KeyboardShortcutCommand] => [
      key,
      ({ editor }) => editor.chain().label(operation, "keyboard", null).toggleMark(mark).run(),
    ]),
  )
}

/** Stock `Bold` (`Mod-b`, `**…**`/`__…__`), reporting `toggle-bold` from keyboard and markdown. */
export const ObservedBold = Bold.extend({
  addKeyboardShortcuts() {
    return markShortcuts(["Mod-b", "Mod-B"], "bold", OPERATION_CONTRACTS.OP_TOGGLE_BOLD)
  },
  addInputRules() {
    return markMarkdownRules(this.parent?.() ?? [], OPERATION_CONTRACTS.OP_TOGGLE_BOLD)
  },
})

/** Stock `Italic` (`Mod-i`, `*…*`/`_…_`), reporting `toggle-italic`. */
export const ObservedItalic = Italic.extend({
  addKeyboardShortcuts() {
    return markShortcuts(["Mod-i", "Mod-I"], "italic", OPERATION_CONTRACTS.OP_TOGGLE_ITALIC)
  },
  addInputRules() {
    return markMarkdownRules(this.parent?.() ?? [], OPERATION_CONTRACTS.OP_TOGGLE_ITALIC)
  },
})

/** Stock `Strike` (`Mod-Shift-s`, `~~…~~`), reporting `toggle-strikethrough`. */
export const ObservedStrike = Strike.extend({
  addKeyboardShortcuts() {
    return markShortcuts(["Mod-Shift-s"], "strike", OPERATION_CONTRACTS.OP_TOGGLE_STRIKETHROUGH)
  },
  addInputRules() {
    return markMarkdownRules(this.parent?.() ?? [], OPERATION_CONTRACTS.OP_TOGGLE_STRIKETHROUGH)
  },
})

/** `CpUnderline` (`Mod-u`), reporting `toggle-underline`. */
export const ObservedUnderline = CpUnderline.extend({
  addKeyboardShortcuts() {
    return markShortcuts(["Mod-u", "Mod-U"], "underline", OPERATION_CONTRACTS.OP_TOGGLE_UNDERLINE)
  },
})

/** Stock `Superscript` (`Mod-.`), reporting `toggle-superscript`. */
export const ObservedSuperscript = Superscript.extend({
  addKeyboardShortcuts() {
    return markShortcuts(["Mod-."], "superscript", OPERATION_CONTRACTS.OP_TOGGLE_SUPERSCRIPT)
  },
})

/** Stock `Subscript` (`Mod-,`), reporting `toggle-subscript`. */
export const ObservedSubscript = Subscript.extend({
  addKeyboardShortcuts() {
    return markShortcuts(["Mod-,"], "subscript", OPERATION_CONTRACTS.OP_TOGGLE_SUBSCRIPT)
  },
})

/** Stock `Highlight` (`Mod-Shift-h`, `==…==`), reporting the highlight the binding asks for. */
export const ObservedHighlight = CpHighlight.extend({
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-h": () =>
        this.editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_SET_HIGHLIGHT, "keyboard", { color: null })
          .toggleHighlight()
          .run(),
    }
  },
  addInputRules() {
    return (this.parent?.() ?? []).map((rule) =>
      observedInputRule(rule, ({ chain }) =>
        chain().label(OPERATION_CONTRACTS.OP_SET_HIGHLIGHT, "markdown", { color: null }).run(),
      ),
    )
  },
})

/** Stock `Paragraph` (`Mod-Alt-0`), reporting the same set-heading fact as the toolbar. */
export const ObservedParagraph = Paragraph.extend({
  addKeyboardShortcuts() {
    return {
      "Mod-Alt-0": () =>
        this.editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_SET_HEADING, "keyboard", { level: null })
          .setParagraph()
          .run(),
    }
  },
})

/**
 * Stock `Heading`, its `Mod-Alt-<level>` shortcuts and `#`/`##` rules re-declared as labeled
 * `set-heading` transactions. Each rule is declared for one level, so the level the label states
 * is the level that rule converts to — nothing is read back off the converted block.
 *
 * NOTE: We also override the keyboard shortcut command, to use our toggleBlockType
 * instead of toggleHeading - as in the toolbar button.
 */
export const ObservedHeading = Heading.extend({
  addKeyboardShortcuts() {
    return Object.fromEntries(
      this.options.levels.map((level) => [
        `Mod-Alt-${level}`,
        () =>
          this.editor
            .chain()
            .label(OPERATION_CONTRACTS.OP_SET_HEADING, "keyboard", { level })
            .command(toggleBlockType("heading", "paragraph", { level }))
            .run(),
      ]),
    )
  },
  addInputRules() {
    return this.options.levels.map((level) =>
      observedInputRule(
        textblockTypeInputRule({
          find: new RegExp(`^(#{1,${level}})\\s$`),
          type: this.type,
          getAttributes: { level },
        }),
        ({ chain }) =>
          chain().label(OPERATION_CONTRACTS.OP_SET_HEADING, "markdown", { level }).run(),
      ),
    )
  },
})

/**
 * The flat-list extensions' stock toggle bindings, re-declared to report `set-list`. Only the
 * toggles are re-declared: the core list extension's Enter/Tab/Shift-Tab are list *mechanics*
 * (indent, dedent, item splitting) with no ratified operation, and stay stock.
 *
 * Each key runs the STOCK `toggleFlatListItem`, only labeled — see ObservedHeading on why the
 * toolbar's `toggleBlockType` must not be substituted here.
 */

/** Stock unordered flat list (`Mod-Shift-8`, `- `), reporting `set-list: bulleted`. */
export const ObservedFlatListUnordered = FlatListUnordered.extend({
  addKeyboardShortcuts(): Record<string, KeyboardShortcutCommand> {
    return {
      "Mod-Shift-8": ({ editor }) =>
        editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_SET_LIST, "keyboard", { list: "bulleted" })
          .toggleFlatListItem(LIST_TYPES.bulleted)
          .run(),
    }
  },
  addInputRules() {
    return listMarkdownRules(this.parent?.() ?? [], "bulleted")
  },
})

/** Stock ordered flat list (`Mod-Shift-7`, `1. `), reporting `set-list: numbered`. */
export const ObservedFlatListOrdered = FlatListOrdered.extend({
  addKeyboardShortcuts(): Record<string, KeyboardShortcutCommand> {
    return {
      "Mod-Shift-7": ({ editor }) =>
        editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_SET_LIST, "keyboard", { list: "numbered" })
          .toggleFlatListItem(LIST_TYPES.numbered)
          .run(),
    }
  },
  addInputRules() {
    return listMarkdownRules(this.parent?.() ?? [], "numbered")
  },
})

/** Stock task flat list (`Mod-Shift-9`, `[ ] `), reporting `set-list: checklist`. */
export const ObservedFlatListTask = FlatListTask.extend({
  addKeyboardShortcuts(): Record<string, KeyboardShortcutCommand> {
    return {
      "Mod-Shift-9": ({ editor }) =>
        editor
          .chain()
          .label(OPERATION_CONTRACTS.OP_SET_LIST, "keyboard", { list: "checklist" })
          .toggleFlatListItem(LIST_TYPES.checklist)
          .run(),
    }
  },
  addInputRules() {
    return listMarkdownRules(this.parent?.() ?? [], "checklist")
  },
})

const reportedPastes = new WeakSet<ClipboardEvent>()

/**
 * THE paste gesture: one clipboard event, one OP_PASTE. The handler changes nothing, so it
 * observes the gesture without competing for it, but it must be *asked* first — an extension
 * that claims the paste (link, image, file upload) ends the prop chain — hence the priority.
 *
 * Paste reporting lives here rather than in `transformPasted`, which prosemirror-view also runs
 * on the dragged slice inside its own handleDrop — a drag would otherwise report pastes that
 * never happened.
 */
export const ObservedPaste = Extension.create({
  name: "observedPaste",
  priority: 1000,

  addProseMirrorPlugins() {
    const { editor } = this
    return [
      new Plugin({
        props: {
          handlePaste(view, event) {
            if (!view.editable) return false
            // doPaste bails when the clipboard has no text/html and prosemirror-view then
            // re-runs it against a hidden capture div with the same event (input.ts
            // capturePaste), so the prop can fire twice for one gesture.
            if (!reportedPastes.has(event)) {
              reportedPastes.add(event)
              reportOperation(editor, OPERATION_CONTRACTS.OP_PASTE, "clipboard", {
                cardId: getContainingCardId(view.state.doc, view.state.selection.from),
              })
            }
            return false
          },
        },
      }),
    ]
  },
})
